import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { SalaryGradeTrancheModal } from '../components/SalaryGradeTrancheModal';
import { salaryGradesApi } from '../plantillaApi';
import { salaryActivationLabel, salaryTypeLabel, type SalaryTrancheListItem } from '../plantillaTypes';
import { salaryTranchePath } from '../utils/salaryTranchePath';

/**
 * The salary schedule, BY TRANCHE (product call, 16 Oct 2026: "display the SG per tranche;
 * inside the tranche are the declared SGs"). One row per type and tranche; the anchor opens the
 * tranche's profile, which holds its grades.
 */
export function SalaryGradesIndexPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    tranche: SalaryTrancheListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const toast = useToast();
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchTranches = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      salaryGradesApi.listTranches(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          status: query.facets.status?.join(','),
          type: query.facets.type?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (tranche: SalaryTrancheListItem): MenuItem[] => [
      {
        key: `restore-${tranche.type}-${tranche.tranche}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'salary-grades.delete',
        disabledReason: tranche.isActive ? 'This tranche is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', tranche }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      const { type, tranche } = confirming.tranche;

      if (confirming.kind === 'deactivate') await salaryGradesApi.deactivateTranche(type, tranche);
      else await salaryGradesApi.activateTranche(type, tranche);

      toast.success(`Tranche ${tranche} ${confirming.kind === 'deactivate' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<SalaryTrancheListItem>
        module="salary-grades"
        breadcrumb={[
          'Human Resources',
          { label: 'Organization', to: '/organization/chart' },
          { label: 'Settings', to: '/organization/settings' },
          'Salary Grades',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'type', label: 'Type' },
        ]}
        // Filed by type: General and Health are what a reader switches between.
        folderNavFacet="type"
        auditEntityTypes={['SalaryGrade']}
        rowKey={(tranche) => `${tranche.type}:${tranche.tranche}`}
        fetch={fetchTranches}
        reloadKey={reloadKey}
        searchPlaceholder="Search tranches"
        emptyMessage="No tranches yet. Declare a tranche's salary grades — the rates a plantilla position's grade is read against."
        onCreate={() => setCreating(true)}
        createPermission="salary-grades.create"
        onRowOpen={(tranche) => navigate(salaryTranchePath(tranche))}
        openPermission="salary-grades.read"
        rowActions={rowActions}
        onDelete={(tranche) => setConfirming({ kind: 'deactivate', tranche })}
        deletePermission="salary-grades.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(tranche) => (tranche.isActive ? null : 'This tranche is already deleted.')}
        onClearData={() => salaryGradesApi.clear()}
        clearDataPermission="salary-grades.clear-data"
        moduleNoun="salary grades"
        otherInfo={(tranche) => tranche}
      />

      {creating ? (
        <SalaryGradeTrancheModal
          open
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete tranche' : 'Restore tranche'}
          danger={confirming.kind === 'deactivate'}
          loading={running}
          id={`salary-tranche-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Tranche ${confirming.tranche.tranche} (${salaryTypeLabel(confirming.tranche.type)}) and its ${confirming.tranche.rateCount} rates will stop being offered when a position is given a grade. Positions already carrying a grade keep it — a position records the grade, not the rate.`
                  : `Tranche ${confirming.tranche.tranche} (${salaryTypeLabel(confirming.tranche.type)}) and its rates will be offered again.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/* No column sorts: the server orders the tranches latest first — the one declared most recently on top. */
const COLUMNS: ColumnDef<SalaryTrancheListItem>[] = [
  {
    /*
      THE ANCHOR MERGES THREE (product call, 16 Oct 2026: "concat the type before the tranche,
      then concat the effective date") — `General — Tranche 1 — Oct 16, 2026`, one line, one
      header, one width. Merged, never duplicated: the type and the date lose their own columns.
      The date keeps the mono face through `trailingCode`, and goes through the calendar-date
      formatter, never the instant one.
    */
    key: 'tranche',
    header: 'Tranche',
    width: 320,
    render: (tranche) => (
      <TextCell
        value={`${salaryTypeLabel(tranche.type)} — Tranche ${tranche.tranche}`}
        trailingCode={formatDateOnly(tranche.effectivityDate)}
      />
    ),
  },
  /*
    THE COUNTS CARRY THEIR NOUN — "30 grades", "240 rates" — because the list view's summary line
    has no labels, and a bare `· 30 · 240` there says nothing. Nominated, so the line does not
    fall back to whatever columns come next.
  */
  {
    key: 'gradeCount',
    header: 'Grades',
    width: 120,
    align: 'right',
    summary: true,
    render: (tranche) => (
      <TextCell value={`${tranche.gradeCount} grade${tranche.gradeCount === 1 ? '' : 's'}`} />
    ),
  },
  {
    key: 'rateCount',
    header: 'Rates',
    width: 120,
    align: 'right',
    flex: true,
    summary: true,
    render: (tranche) => (
      <TextCell value={`${tranche.rateCount} rate${tranche.rateCount === 1 ? '' : 's'}`} />
    ),
  },
  {
    // The LIFECYCLE beside the live-or-deleted pill: Draft is read by nothing outside this page.
    key: 'activationStatus',
    header: 'Activation status',
    width: 130,
    stage: true,
    render: (tranche) => <StatusText tone="inactive">{salaryActivationLabel(tranche.activationStatus)}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (tranche) =>
      tranche.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
